import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Button, Modal } from "antd";
import { NotificationManager } from "react-notifications";
import AdminToolsDataService from "../../services/admin-tools.service";

const FEATURE_LABEL = {
  reports: "Reports",
  marketing: "Marketing",
  po: "Purchase Orders",
};

// Vendor Feature Lock (ADO #1597) — "Request access" modal shown from the
// locked Reports/Marketing/PO page's CTA. Same shape/pattern as
// CoreUpgradeModal, posting to its own endpoint (SendFeatureAccessRequest)
// since the email copy is feature-lock specific, not a Core upgrade.
const RequestAccessModal = ({ visible, onClose, feature }) => {
  const { t } = useTranslation();
  const loginDetials = useSelector((state) => state.loginReducer);
  const label = FEATURE_LABEL[feature] || feature;

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    companyName: "",
    message: "",
  });

  const [validation, setValidation] = useState({
    firstNameVal: "",
    lastNameVal: "",
    emailVal: "",
    companyNameVal: "",
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (
      loginDetials &&
      loginDetials.loginDetials &&
      loginDetials.loginDetials.responseData
    ) {
      const userData = loginDetials.loginDetials.responseData;

      let firstName = "";
      let lastName = "";
      if (userData.userProfilename) {
        const nameParts = userData.userProfilename.split(" ");
        firstName = nameParts[0] || "";
        lastName = nameParts.slice(1).join(" ") || "";
      }

      setFormData((prev) => ({
        ...prev,
        firstName: firstName,
        lastName: lastName,
        email: userData.userEmail || "",
        companyName: userData.dealerCompany || "",
      }));
    }
  }, [loginDetials, visible]);

  const handleInputChange = (e, field) => {
    const value = e.target.value;
    setFormData({
      ...formData,
      [field]: value,
    });
    if (validation[`${field}Val`]) {
      setValidation({
        ...validation,
        [`${field}Val`]: "",
      });
    }
  };

  const validateForm = () => {
    const newValidation = {
      firstNameVal: "",
      lastNameVal: "",
      emailVal: "",
      companyNameVal: "",
    };
    let isValid = true;

    if (!formData.firstName.trim()) {
      newValidation.firstNameVal = t("First Name is required");
      isValid = false;
    }
    if (!formData.lastName.trim()) {
      newValidation.lastNameVal = t("Last Name is required");
      isValid = false;
    }
    if (!formData.email.trim()) {
      newValidation.emailVal = t("Email Address is required");
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newValidation.emailVal = t("Please enter a valid email address");
      isValid = false;
    }
    if (!formData.companyName.trim()) {
      newValidation.companyNameVal = t("Company Name is required");
      isValid = false;
    }

    setValidation(newValidation);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    setLoading(true);
    try {
      let inputData = {
        contactName: `${formData.firstName} ${formData.lastName}`.trim(),
        companyName: formData.companyName,
        email: formData.email,
        message: formData.message,
        feature: label,
        dealerId: loginDetials?.loginDetials?.responseData?.dealerId || 0,
      };

      const response = await AdminToolsDataService.SendFeatureAccessRequest(
        inputData
      );

      if (response.data.message === "Success") {
        NotificationManager.success(
          t("Your request has been sent. Our support team will be in touch."),
          t("Success"),
          3000
        );
        setFormData((prev) => ({ ...prev, message: "" }));
        if (onClose) {
          onClose();
        }
      } else {
        NotificationManager.error(
          t("Failed to send request. Please try again."),
          t("Error"),
          3000
        );
      }
    } catch (error) {
      console.error("Error sending feature access request:", error);
      NotificationManager.error(
        t("Failed to send request. Please try again."),
        t("Error"),
        3000
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={t(`Request access — ${label}`)}
      centered
      visible={visible}
      onCancel={onClose}
      width={600}
      footer={[
        <Button key="back" onClick={onClose}>
          {t("Cancel")}
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleSubmit}
        >
          {t("Send request to support")}
        </Button>,
      ]}
    >
      <div className="form__fields border-0 p-0">
        <p className="mb-4">
          {t(
            `${label} is available on a paid subscription. Get in touch with our support team using the form below to upgrade.`
          )}
        </p>
        <div className="row">
          <div className="col-lg-6">
            <div className="input__block">
              <label>
                {t("First Name")} <span className="mandatory">*</span>
              </label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => handleInputChange(e, "firstName")}
                className={validation.firstNameVal && "border__red"}
                placeholder={t("Enter your first name")}
              />
              {validation.firstNameVal && (
                <p className="error-color-red">{validation.firstNameVal}</p>
              )}
            </div>
          </div>
          <div className="col-lg-6">
            <div className="input__block">
              <label>
                {t("Last Name")} <span className="mandatory">*</span>
              </label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => handleInputChange(e, "lastName")}
                className={validation.lastNameVal && "border__red"}
                placeholder={t("Enter your last name")}
              />
              {validation.lastNameVal && (
                <p className="error-color-red">{validation.lastNameVal}</p>
              )}
            </div>
          </div>
          <div className="col-lg-6">
            <div className="input__block">
              <label>
                {t("Email Address")} <span className="mandatory">*</span>
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange(e, "email")}
                className={validation.emailVal && "border__red"}
                placeholder={t("Enter your email address")}
              />
              {validation.emailVal && (
                <p className="error-color-red">{validation.emailVal}</p>
              )}
            </div>
          </div>
          <div className="col-lg-6">
            <div className="input__block">
              <label>
                {t("Company Name")} <span className="mandatory">*</span>
              </label>
              <input
                type="text"
                value={formData.companyName}
                onChange={(e) => handleInputChange(e, "companyName")}
                className={validation.companyNameVal && "border__red"}
                placeholder={t("Enter your company name")}
              />
              {validation.companyNameVal && (
                <p className="error-color-red">{validation.companyNameVal}</p>
              )}
            </div>
          </div>
          <div className="col-lg-12">
            <div className="input__block">
              <label>{t("Message")}</label>
              <textarea
                rows="4"
                value={formData.message}
                onChange={(e) => handleInputChange(e, "message")}
                placeholder={t("Tell us a bit about what you need.")}
              ></textarea>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default RequestAccessModal;
